import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import {
  FiArrowRight,
  FiDownload,
  FiX,
  FiMail,
  FiMapPin,
  FiCalendar,
  FiBriefcase,
  FiStar,
  FiExternalLink,
  FiEdit3,
  FiSave,
  FiCpu,
  FiHeart,
  FiUsers,
  FiZap,
  FiCode,
  FiMousePointer,
} from "react-icons/fi";
import {
  FaReact,
  FaNodeJs,
  FaGithub,
  FaJava,
  FaInstagram,
  FaWhatsapp,
  FaLinkedin,
  FaGraduationCap,
} from "react-icons/fa";
import { SiMongodb, SiMysql, SiFirebase, SiTailwindcss, SiLeetcode } from "react-icons/si";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import Navbar from "./Navbar";
import Foot from "./Foot";
import "./Hero.css";   // design system: tokens, buttons, grain, .kk-progress
import "./About.css";  // everything unique to this page

/* ═══════════════ DATA ═══════════════ */

const ADMIN_EMAIL = "karanksxxx@gmail.com";

const DEFAULT_BIO = `I'm Karan Kumar, a BCA student and aspiring Software Engineer. I love turning ideas into real-world applications and growing as a developer. I believe in learning by building — every project teaches me something new, and every challenge makes me better at solving real-world problems through code.`;

const TRAITS = [
  { icon: <FiCpu />, title: "Problem solver", desc: "I love finding solutions to complex problems." },
  { icon: <FiHeart />, title: "Fast learner", desc: "Always curious and eager to learn." },
  { icon: <FiUsers />, title: "Team player", desc: "I enjoy working with others." },
  { icon: <FiZap />, title: "Creative thinker", desc: "I bring creativity to real-world solutions." },
];

const JOURNEY = [
  { year: "2024", title: "Started BCA", desc: "D.A.V College, Bathinda" },
  { year: "2025", title: "First internship", desc: "Learning & building" },
  { year: "2025", title: "Building projects", desc: "Real world experience" },
  { year: "2027", title: "Graduate BCA", desc: "Goal — software engineer" },
];

const PHILOSOPHY = [
  { title: "Quality first", desc: "Committed to high-quality code that is maintainable and efficient, providing real value." },
  { title: "Continuous growth", desc: "Every problem faced is an opportunity to grow both professionally and as a technical individual." },
  { title: "Resilience", desc: "Resilient when faced with failure — each challenge solved makes you more knowledgeable." },
  { title: "Adaptability", desc: "In an ever-changing tech world, flexibility, curiosity and diligent learning are paramount." },
];

const techIcons = [
  { icon: <FaReact />, name: "React", color: "#61DAFB", image: "https://cloudmatetechnologies.com/wp-content/uploads/2024/06/react.js.png" },
  { icon: <FaNodeJs />, name: "Node.js", color: "#339933", image: "https://www.mindrops.com/images/nodejs-image.webp" },
  { icon: <SiMongodb />, name: "MongoDB", color: "#47A248", image: "https://www.opc-router.de/wp-content/uploads/2024/03/mongodb_thumbnail.png" },
  { icon: <SiMysql />, name: "MySQL", color: "#4479A1", image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT7VofdmPnt1ZYROHeZIHLROMfi5jVQiNq61A&s" },
  { icon: <FaGithub />, name: "GitHub", color: "#e6edff", image: "https://beecrowd.com/wp-content/uploads/2024/04/2022-08-18-GitHub.jpg" },
  { icon: <SiFirebase />, name: "Firebase", color: "#FFCA28", image: "https://www.gstatic.com/devrel-devsite/prod/v46d043083f27fa7361aea8506dabbd161e0b84f5a7c6df8d5e3cfad447dd4376/firebase/images/touchicon-180.png" },
  { icon: <FaJava />, name: "Java", color: "#ED8B00", image: "https://logolook.net/wp-content/uploads/2022/11/Java-Logo.png" },
  { icon: <SiTailwindcss />, name: "Tailwind", color: "#06B6D4", image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSW6eyjw-sncwZxtTnaQj66DdHF2PJ0uL6PGQ&s" },
];

const SOCIALS = [
  { icon: <FaGithub />, href: "https://github.com/Karanks1436", label: "GitHub" },
  { icon: <FaLinkedin />, href: "https://linkedin.com", label: "LinkedIn" },
  { icon: <FaInstagram />, href: "https://www.instagram.com/karanks1436", label: "Instagram" },
  { icon: <FiMail />, href: "mailto:karanksxxx@gmail.com", label: "Email" },
  { icon: <FaWhatsapp />, href: "https://wa.me/919041429065", label: "WhatsApp" },
  { icon: <SiLeetcode />, href: "https://leetcode.com/u/karanks1436/", label: "LeetCode" },
];

/* ═══════════════ ONE rAF LOOP: [data-plx] + hero + pointer ═══════════════ */

function usePageMotion(rootRef, heroRef, copyRef, photoRef, ready) {
  useEffect(() => {
    if (!ready) return;
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const items = Array.from(root.querySelectorAll("[data-plx]"));
    const tilts = Array.from(root.querySelectorAll("[data-mouse]"));
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    let px = 0;
    let py = 0;
    let raf = 0;

    const onPointer = (e) => {
      px = e.clientX / window.innerWidth - 0.5;
      py = e.clientY / window.innerHeight - 0.5;
    };

    const render = () => {
      const vh = window.innerHeight;

      for (const el of items) {
        const speed = parseFloat(el.dataset.plx) || 0;
        const xSpeed = parseFloat(el.dataset.plxX) || 0;
        const r = el.getBoundingClientRect();
        if (r.bottom < -420 || r.top > vh + 420) continue;
        const y = (r.top + r.height / 2 - vh / 2) * -speed;
        const x = xSpeed ? (r.left + r.width / 2 - window.innerWidth / 2) * -xSpeed : 0;
        el.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0)`;
      }

      if (fine) {
        for (const el of tilts) {
          const s = parseFloat(el.dataset.mouse) || 10;
          el.style.transform = `translate3d(${(px * s).toFixed(2)}px, ${(py * s * 0.8).toFixed(2)}px, 0)`;
        }
      }

      const hero = heroRef.current;
      if (hero) {
        const h = hero.offsetHeight || 1;
        const p = Math.min(1, Math.max(0, -hero.getBoundingClientRect().top / h));
        if (copyRef.current) {
          copyRef.current.style.opacity = String(Math.max(0, 1 - p * 1.45));
          copyRef.current.style.transform = `translate3d(0, ${(-p * 64).toFixed(1)}px, 0)`;
        }
        if (photoRef.current) {
          photoRef.current.style.transform = `translate3d(0, ${(-p * 26).toFixed(1)}px, 0) scale(${(1 + p * 0.06).toFixed(3)})`;
        }
      }

      raf = requestAnimationFrame(render);
    };

    if (fine) window.addEventListener("mousemove", onPointer, { passive: true });
    raf = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onPointer);
    };
  }, [ready, rootRef, heroRef, copyRef, photoRef]);
}

/* ═══════════════ SMALL COMPONENTS ═══════════════ */

function FloatingParticles() {
  const parts = useRef(
    Array.from({ length: 26 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      s: Math.random() * 3 + 1.5,
      d: Math.random() * 10 + 8,
      dl: Math.random() * 5,
    }))
  );
  return (
    <div className="ab-particles" aria-hidden="true">
      {parts.current.map((p) => (
        <motion.span
          key={p.id}
          className="ab-particle"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.s, height: p.s }}
          animate={{ y: [0, -24, 0], opacity: [0.08, 0.45, 0.08] }}
          transition={{ duration: p.d, repeat: Infinity, delay: p.dl, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

function Counter({ to, suffix = "" }) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const ob = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setStarted(true);
      },
      { threshold: 0.4 }
    );
    if (ref.current) ob.observe(ref.current);
    return () => ob.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    const dur = 1400;
    const t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, to]);

  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
}

/* portrait with a graceful fallback if /kks1436.png is missing */
function Photo({ src, alt }) {
  const [ok, setOk] = useState(true);
  return ok ? (
    <img src={src} alt={alt} onError={() => setOk(false)} />
  ) : (
    <img src="/img/hero-portrait.jpg" alt={alt} />
  );
}

/* ─── Interactive canvas: bouncing tech icons (your code, re-themed) ─── */
function TechCanvas({ techs }) {
  const canvasRef = useRef(null);
  const iconBank = useRef(null);
  const mouse = useRef({ x: null, y: null });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    let balls = [];
    let raf;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const W = () => canvas.width / dpr;
    const H = () => canvas.height / dpr;

    class Ball {
      constructor(tech, i) {
        this.radius = 38;
        this.name = tech.name;
        this.color = tech.color;
        this.initials = tech.name.replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase();
        this.x = Math.random() * Math.max(1, W() - this.radius * 2) + this.radius;
        this.y = Math.random() * Math.max(1, H() - this.radius * 2) + this.radius;
        this.dx = (Math.random() - 0.5) * 1.4;
        this.dy = (Math.random() - 0.5) * 1.4;
        this.loaded = false;
        this.img = new Image();
        this.img.onload = () => {
          this.loaded = true;
        };
        this.img.onerror = () => {
          this.loaded = false;
        };
        /* use the real react-icon glyph: the icons are rendered once into a
           hidden node, then serialised to an inline SVG data URI — no network
           request, no CORS taint, identical in every browser */
        const node = iconBank.current?.querySelector(`svg[data-ic="${i}"]`);
        if (node) {
          let markup = node.outerHTML;
          if (!/xmlns=/.test(markup)) {
            markup = markup.replace("<svg ", '<svg xmlns="http://www.w3.org/2000/svg" ');
          }
          this.img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(markup);
        }
      }

      draw() {
        ctx.save();
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(11,11,16,.9)";
        ctx.fill();

        if (this.loaded && this.img.complete && this.img.naturalWidth > 0) {
          const s = this.radius * 1.18;
          ctx.drawImage(this.img, this.x - s / 2, this.y - s / 2, s, s);
        } else {
          ctx.fillStyle = this.color;
          ctx.font = `600 ${this.radius * 0.62}px Sora, sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(this.initials, this.x, this.y + 1);
        }
        ctx.restore();

        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(200,255,61,.45)";
        ctx.lineWidth = 1.5;
        ctx.shadowColor = "rgba(200,255,61,.55)";
        ctx.shadowBlur = 16;
        ctx.stroke();
        ctx.shadowBlur = 0;

        const g = ctx.createRadialGradient(
          this.x - this.radius / 3,
          this.y - this.radius / 3,
          this.radius * 0.1,
          this.x,
          this.y,
          this.radius
        );
        g.addColorStop(0, "rgba(255,255,255,.12)");
        g.addColorStop(1, "rgba(0,0,0,.34)");
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
      }

      update() {
        this.x += this.dx;
        this.y += this.dy;

        if (this.x + this.radius > W() || this.x - this.radius < 0) {
          this.dx *= -0.95;
          this.x = Math.max(this.radius, Math.min(W() - this.radius, this.x));
        }
        if (this.y + this.radius > H() || this.y - this.radius < 0) {
          this.dy *= -0.95;
          this.y = Math.max(this.radius, Math.min(H() - this.radius, this.y));
        }

        balls.forEach((o) => {
          if (this === o) return;
          const dx = this.x - o.x;
          const dy = this.y - o.y;
          const dist = Math.hypot(dx, dy);
          if (dist < this.radius + o.radius && dist > 0.01) {
            const a = Math.atan2(dy, dx);
            const overlap = (this.radius + o.radius - dist) * 0.5;
            this.x += Math.cos(a) * overlap;
            this.y += Math.sin(a) * overlap;
            o.x -= Math.cos(a) * overlap;
            o.y -= Math.sin(a) * overlap;
            const tdx = this.dx;
            const tdy = this.dy;
            this.dx = o.dx * 0.95;
            this.dy = o.dy * 0.95;
            o.dx = tdx * 0.95;
            o.dy = tdy * 0.95;
          }
        });

        if (mouse.current.x !== null) {
          const rect = canvas.getBoundingClientRect();
          const mx = mouse.current.x - rect.left;
          const my = mouse.current.y - rect.top;
          const dmx = mx - this.x;
          const dmy = my - this.y;
          const d = Math.hypot(dmx, dmy);
          if (d < 200) {
            const f = ((200 - d) / 200) * 0.008;
            this.x += dmx * f;
            this.y += dmy * f;
          }
        }

        this.draw();
      }
    }

    balls = techs.map((t, i) => new Ball(t, i));
    const animate = () => {
      ctx.clearRect(0, 0, W(), H());
      balls.forEach((b) => b.update());
      raf = requestAnimationFrame(animate);
    };
    animate();

    const onMove = (e) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
    };
    const onLeave = () => {
      mouse.current.x = null;
      mouse.current.y = null;
    };

    window.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);
    window.addEventListener("resize", resize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("resize", resize);
    };
  }, [techs]);

  return (
    <>
      <canvas ref={canvasRef} className="ab-canvas" />
      {/* icons are rendered once here, then serialised into the canvas as
          inline SVG data URIs — no external image requests at all */}
      <div className="ab-canvas-icons" ref={iconBank} aria-hidden="true">
        {techs.map((t, i) => (
          <span key={`${t.name}-${i}`} data-ic={i}>
            {React.cloneElement(t.icon, { color: t.color, size: 48, "data-ic": i })}
          </span>
        ))}
      </div>
    </>
  );
}

/* ─── Edit modal ─── */
function EditModal({ biodata, onChange, onSave, onClose }) {
  return (
    <motion.div
      className="ab-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="ab-modal"
        initial={{ scale: 0.94, y: 24 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.94, y: 24 }}
        transition={{ type: "spring", damping: 26, stiffness: 260 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ab-modal-head">
          <div>
            <span className="kk-eyebrow">Admin</span>
            <h3>Edit profile</h3>
          </div>
          <button className="ab-modal-x" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        <div className="ab-modal-body">
          {[
            { label: "Name", name: "name", ph: "Your name" },
            { label: "Date of birth", name: "dob", ph: "DD/MM/YYYY" },
            { label: "Location", name: "location", ph: "City, Country" },
            { label: "Experience", name: "experience", ph: "e.g. 1+ Years" },
            { label: "Specialty", name: "specialty", ph: "e.g. Full Stack Development" },
          ].map((f) => (
            <label key={f.name} className="ab-field">
              <span>{f.label}</span>
              <input
                type="text"
                name={f.name}
                value={biodata[f.name] || ""}
                onChange={onChange}
                placeholder={f.ph}
              />
            </label>
          ))}

          <label className="ab-field ab-field--full">
            <span>Bio</span>
            <textarea
              name="bio"
              rows={4}
              value={biodata.bio || ""}
              onChange={onChange}
              placeholder="Short bio for the About page..."
            />
          </label>
        </div>

        <div className="ab-modal-foot">
          <button className="kk-btn kk-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="kk-btn" onClick={onSave}>
            <FiSave /> Save changes
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ═══════════════ ABOUT PAGE ═══════════════ */

export default function About() {
  const rootRef = useRef(null);
  const heroRef = useRef(null);
  const copyRef = useRef(null);
  const photoRef = useRef(null);

  const [userEmail, setUserEmail] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [biodata, setBiodata] = useState({
    name: "",
    dob: "",
    location: "",
    experience: "",
    specialty: "",
    bio: "",
  });

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28 });

  usePageMotion(rootRef, heroRef, copyRef, photoRef, !loading);

  /* ─── Firebase (optional — falls back to defaults in preview) ─── */
  useEffect(() => {
    // preview / logged-out fallback: same localStorage key as Projects.jsx
    try {
      const cached = JSON.parse(localStorage.getItem("user") || "null");
      if (cached?.Email) setUserEmail(cached.Email);
    } catch {
      /* ignore */
    }

    if (!auth || !db) {
      setLoading(false);
      return;
    }

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserEmail(user.email || "");
        try {
          const snap = await getDoc(doc(db, "About", "karan"));
          if (snap.exists()) setBiodata((p) => ({ ...p, ...snap.data() }));
        } catch (e) {
          console.error("About fetch error:", e);
        }
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleChange = (e) => setBiodata({ ...biodata, [e.target.name]: e.target.value });

  const handleSave = async () => {
    if (db) {
      try {
        await setDoc(doc(db, "About", "karan"), biodata);
      } catch (e) {
        console.error("About save error:", e);
      }
    }
    setShowModal(false);
  };

  const name = biodata.name || "Karan Kumar";
  const firstName = name.split(" ")[0];

  const details = [
    { icon: <FiCalendar />, label: "Date of birth", value: biodata.dob || "Not specified" },
    { icon: <FiMapPin />, label: "Location", value: biodata.location || "Punjab, India" },
    { icon: <FiBriefcase />, label: "Experience", value: biodata.experience || "1+ Years" },
    { icon: <FiStar />, label: "Specialty", value: biodata.specialty || "Full Stack Development" },
  ];

  const stats = [
    { n: 1, s: "+", label: "Years experience", icon: <FiBriefcase /> },
    { n: 5, s: "+", label: "Projects completed", icon: <FiStar /> },
    { n: 10, s: "+", label: "Happy clients", icon: <FiMail /> },
    { n: 10, s: "+", label: "Technologies", icon: <FiExternalLink /> },
  ];

  if (loading) {
    return (
      <div className="kk-root ab-loading">
        <div className="ab-spinner" />
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="kk-root ab-page" ref={rootRef}>
      <motion.div className="kk-progress" style={{ scaleX: progress }} />
      <FloatingParticles />

      {/* ══════════════ HERO ══════════════ */}
      <header className="ab-hero" ref={heroRef}>
        <div className="ab-hero__bg" aria-hidden="true">
          <span className="ab-hero__grid" data-plx="0.05" />
          <span className="ab-hero__glow ab-hero__glow--acid" data-plx="0.16" />
          <span className="ab-hero__glow ab-hero__glow--violet" data-plx="0.24" />
          <span className="ab-hero__word" data-plx="0.1">
            ABOUT
          </span>
        </div>

        <div className="kk-wrap ab-hero__in">
          <div className="ab-hero__copy" ref={copyRef}>
            <motion.div
              className="ab-kicker"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <span className="kk-dot" />
              <span>About me</span>
              <span className="kk-slash">/</span>
              <span>Software Engineer</span>
              <span className="kk-slash">/</span>
              <span>Bathinda, IN</span>
            </motion.div>

            <h1 className="ab-title">
              <motion.span
                className="ab-title__line"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              >
                Hi, I&apos;m {firstName}
              </motion.span>
              <motion.span
                className="ab-title__line ab-title__line--serif"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                the person behind the pixels<span className="kk-acid">.</span>
              </motion.span>
            </h1>

            <motion.p
              className="ab-lead"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              {biodata.bio || DEFAULT_BIO}
            </motion.p>

            <motion.div
              className="ab-chips"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
            >
              <span>
                <FiMapPin /> {biodata.location || "Bathinda, Punjab"}
              </span>
              <span>
                <FaGraduationCap /> BCA · 2024 – 2027
              </span>
              <span>
                <FiMail /> karanksxxx@gmail.com
              </span>
            </motion.div>

            <motion.div
              className="ab-actions"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.48 }}
            >
              <Link className="kk-btn kk-btn--lg" to="/contact">
                Work with me <FiArrowRight />
              </Link>
              <a className="kk-btn kk-btn--ghost kk-btn--lg" href="/projects">
                See the work <FiArrowRight />
              </a>
              {userEmail === ADMIN_EMAIL && (
                <button
                  className="ab-edit"
                  onClick={() => setShowModal(true)}
                  type="button"
                >
                  <FiEdit3 /> Edit profile
                </button>
              )}
            </motion.div>

            <div className="ab-hero__socials">
              <span className="ab-hero__socials-label">Elsewhere</span>
              {SOCIALS.slice(0, 5).map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  title={s.label}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* portrait */}
          <div className="ab-photo-col">
            <div className="ab-photo-tilt" data-mouse="12">
              <motion.div
                className="ab-photo"
                ref={photoRef}
                initial={{ opacity: 0, scale: 0.94, y: 24 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                <Photo src="/kks1436.png" alt={name} />
                <span className="ab-photo__frame" aria-hidden="true" />
                <span className="ab-photo__badge">
                  <i /> Open to work
                </span>
                <figcaption className="ab-photo__cap">
                  <b>{name}</b>
                  <span>{biodata.specialty || "Full Stack Developer"}</span>
                </figcaption>
              </motion.div>
            </div>

            <div className="ab-sign">
              <span className="kk-serif">{name}</span>
              <em>KKS · 1436</em>
            </div>
          </div>
        </div>
      </header>

      {/* ══════════════ TRAITS — full-bleed strip ══════════════ */}
      <section className="ab-traits" aria-label="Traits">
        {TRAITS.map((t, k) => (
          <motion.div
            key={t.title}
            className="ab-trait"
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: k * 0.07, duration: 0.5 }}
          >
            <span className="ab-trait__ic">{t.icon}</span>
            <h4>{t.title}</h4>
            <p>{t.desc}</p>
            <span className="ab-trait__no">0{k + 1}</span>
          </motion.div>
        ))}
      </section>

      {/* ══════════════ DETAILS — full-bleed band ══════════════ */}
      <section className="ab-facts">
        <div className="kk-wrap ab-facts__in">
          <div className="ab-facts__head">
            <span className="kk-eyebrow">01 — Details</span>
            <span className="ab-facts__hint">Everything on the résumé, minus the fluff</span>
          </div>

          <div className="ab-details">
            {details.map((d, k) => (
              <motion.div
                key={d.label}
                className="ab-detail"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: k * 0.06, duration: 0.5 }}
              >
                <span className="ab-detail__no">{String(k + 1).padStart(2, "0")}</span>
                <span className="ab-detail__ic">{d.icon}</span>
                <span className="ab-detail__label">{d.label}</span>
                <b>{d.value}</b>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ STATS ══════════════ */}
      <section className="ab-stats" aria-label="Numbers">
        <div className="kk-wrap ab-stats__in">
          {stats.map((s, k) => (
            <motion.div
              key={s.label}
              className="ab-stat"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ delay: k * 0.06, duration: 0.5 }}
            >
              <span className="ab-stat__ic">{s.icon}</span>
              <span className="ab-stat__num">
                <Counter to={s.n} suffix={s.s} />
              </span>
              <span className="ab-stat__label">{s.label}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ══════════════ TECH PLAYGROUND ══════════════ */}
      <section className="ab-play">
        <div className="ab-play__glow" data-plx="0.12" aria-hidden="true" />

        <div className="kk-wrap">
          <div className="ab-head">
            <div>
              <span className="kk-eyebrow">02 — Stack</span>
              <h2 className="kk-h2">
                Technologies I
                <br />
                <span className="kk-serif kk-acid">work with</span>
              </h2>
            </div>
            <div className="ab-head__aside">
              <p>
                Eight tools that cover the whole loop — interface, API, data and deploy.
                Move your cursor over the playground: the icons are magnetic.
              </p>
              <span className="ab-head__meta">
                <FiMousePointer /> Interactive canvas
              </span>
            </div>
          </div>

          <motion.div
            className="ab-canvas-wrap"
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
          >
            <TechCanvas techs={techIcons} />
            <span className="ab-canvas-frame" aria-hidden="true" />
            <span className="ab-canvas-tag">Fig. 01 — Tech playground</span>
            <span className="ab-canvas-hint">hover / move</span>
          </motion.div>

          <div className="ab-tech-row">
            {techIcons.map((t, k) => (
              <motion.div
                key={t.name}
                className="ab-tech"
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: (k % 4) * 0.05, duration: 0.4 }}
              >
                <span className="ab-tech__ic" style={{ color: t.color }}>
                  {t.icon}
                </span>
                <span className="ab-tech__name">{t.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ JOURNEY — full-bleed band ══════════════ */}
      <section className="ab-journey">
        <div className="ab-journey__bg" aria-hidden="true">
          <span className="ab-journey__orb" data-plx="0.14" />
          <span className="ab-journey__grid" data-plx="0.05" />
        </div>

        <div className="kk-wrap">
          <div className="ab-head">
            <div>
              <span className="kk-eyebrow kk-eyebrow--violet">03 — Journey</span>
              <h2 className="kk-h2">
                The road
                <br />
                <span className="kk-serif">so far</span>
              </h2>
            </div>
            <div className="ab-head__aside">
              <p>
                From the first <code>&lt;div&gt;</code> in 2020 to shipping client work —
                four checkpoints that shaped how I build.
              </p>
            </div>
          </div>

          <div className="ab-tl">
            <span className="ab-tl__line" aria-hidden="true" />
            {JOURNEY.map((j, k) => (
              <motion.div
                key={`${j.year}-${j.title}`}
                className="ab-tl__node"
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: k * 0.1, duration: 0.55 }}
              >
                <span className="ab-tl__dot" />
                <span className="ab-tl__year" data-plx="0.03">
                  {j.year}
                </span>
                <h4>{j.title}</h4>
                <p>{j.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="ab-journey__btns">
            <a href="/codes" className="kk-btn kk-btn--ghost">
              <FiDownload /> View codes
            </a>
            <Link to="/contact" className="kk-btn">
              Contact me <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════ PHILOSOPHY ══════════════ */}
      <section className="ab-phil">
        <div className="kk-wrap ab-phil__grid">
          <div className="ab-phil__copy">
            <span className="kk-eyebrow">04 — Philosophy</span>
            <h2 className="kk-h2">
              My approach to
              <br />
              <span className="kk-serif kk-acid">development</span>
            </h2>

            <div className="ab-phil__list">
              {PHILOSOPHY.map((p, k) => (
                <motion.div
                  key={p.title}
                  className="ab-phil__item"
                  initial={{ opacity: 0, x: -18 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ delay: k * 0.08, duration: 0.5 }}
                >
                  <span className="ab-phil__num">{String(k + 1).padStart(2, "0")}</span>
                  <div>
                    <h4>{p.title}</h4>
                    <p>{p.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            className="ab-code"
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
          >
            <div className="ab-code__head">
              <div className="ab-code__dots">
                <span className="red" />
                <span className="yel" />
                <span className="grn" />
              </div>
              <span className="ab-code__file">
                <FiCode /> philosophy.js
              </span>
            </div>
            <div className="ab-code__body">
              <p>
                <span className="ck">const</span> <span className="cv">philosophy</span> = {"{"}
              </p>
              <p>
                {"  "}
                <span className="ck">approach</span>: <span className="cs">"Quality over Quantity"</span>,
              </p>
              <p>
                {"  "}
                <span className="ck">mindset</span>: <span className="cs">"Always Learning"</span>,
              </p>
              <p>
                {"  "}
                <span className="ck">goal</span>: <span className="cs">"Build Impactful Software"</span>,
              </p>
              <p>
                {"  "}
                <span className="ck">values</span>: [
              </p>
              <p>
                {"    "}
                <span className="cs">"Integrity"</span>,
              </p>
              <p>
                {"    "}
                <span className="cs">"Innovation"</span>,
              </p>
              <p>
                {"    "}
                <span className="cs">"Excellence"</span>
              </p>
              <p>{"  ]"}</p>
              <p>{"}"};</p>
              <p className="ab-code__cmt">{"// Turning vision into reality..."}</p>
              <motion.span
                className="ab-code__cursor"
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.8, repeat: Infinity }}
              >
                ▊
              </motion.span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════ CTA ══════════════ */}
      <section className="ab-cta">
        <div className="ab-cta__orbs" aria-hidden="true">
          <span data-plx="0.1" />
          <span data-plx="0.18" />
        </div>

        <div className="kk-wrap ab-cta__in">
          <span className="kk-eyebrow">Got an idea?</span>
          <h2 className="kk-h2">
            Let&apos;s build something{" "}
            <span className="kk-serif kk-acid">together.</span>
          </h2>
          <p>
            Have a project in mind or want to collaborate? I&apos;d love to hear from you.
          </p>
          <div className="ab-cta__btns">
            <a href="mailto:karanksxxx@gmail.com" className="kk-btn kk-btn--lg">
              <FiMail /> Get in touch
            </a>
            <a href="/projects" className="kk-btn kk-btn--ghost kk-btn--lg">
              View projects <FiExternalLink />
            </a>
          </div>
        </div>
      </section>

      <a
        className="kk-wa"
        href="https://wa.me/919041429065"
        target="_blank"
        rel="noreferrer"
        title="Chat on WhatsApp"
      >
        <FaWhatsapp />
      </a>

      <Navbar />
      <Foot />

      <AnimatePresence>
        {showModal && (
          <EditModal
            biodata={biodata}
            onChange={handleChange}
            onSave={handleSave}
            onClose={() => setShowModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}